import React, { useEffect, useState, useCallback } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import FileUpload from "@/components/admin/FileUpload";
import { can, MANAGE_WATCHES } from "@/lib/format";
import { toast } from "sonner";
import { Search, Watch as WatchIcon, Lock, ImageOff } from "lucide-react";

// Quick primary-photo swap for watches, straight from the CMS.
// Uses the existing admin endpoints: GET /admin/watches and
// PUT /admin/watches/{id} { public_photos } (RBAC: MANAGE_WATCHES).
export default function WatchImageManager() {
  const { user } = useAdminAuth();
  const canManage = can(user?.role, MANAGE_WATCHES);
  const [q, setQ] = useState("");
  const [watches, setWatches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const r = await api.get("/admin/watches", { params: query ? { q: query } : {} });
      setWatches(r.data || []);
    } catch {
      setWatches([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // debounced search
  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q, load]);

  const swap = async (w, fileRef) => {
    setBusyId(w.id);
    try {
      const rest = (w.public_photos || []).slice(1);
      const next = [fileRef, ...rest];
      await api.put(`/admin/watches/${w.id}`, { public_photos: next });
      setWatches((ws) => ws.map((x) => (x.id === w.id ? { ...x, public_photos: next } : x)));
      toast.success("Primary photo updated \u2014 live on the site.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update photo");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="cms-watch-images">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-wide text-[var(--muted)]">Swap a watch's primary photo</div>
        {!canManage && (
          <Badge variant="outline" className="bg-[var(--panel-2)] text-[var(--text-2)] border-[var(--border-lch)]">
            <Lock size={11} className="mr-1" />Needs inventory access
          </Badge>
        )}
      </div>

      <div className="relative max-w-md mb-5">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by brand, model or reference…"
          className="bg-[var(--panel)] pl-9"
          data-testid="cms-watch-search"
        />
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cms-watch-loading">
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-28 rounded-lg bg-[var(--panel-2)] animate-pulse" />)}
        </div>
      ) : !watches || watches.length === 0 ? (
        <div className="text-center text-[var(--muted)] py-16" data-testid="cms-watch-empty">
          <WatchIcon size={26} className="mx-auto mb-2 opacity-60" />
          No watches found.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cms-watch-grid">
          {watches.map((w) => {
            const primary = (w.public_photos || [])[0];
            return (
              <div key={w.id} data-testid={`cms-watch-${w.id}`} className="rounded-lg border border-[var(--border-lch)] bg-[var(--panel)] p-3 flex gap-3">
                <div className="h-20 w-20 shrink-0 rounded bg-[var(--panel-2)] overflow-hidden border border-[var(--border-lch)] flex items-center justify-center">
                  {primary?.url ? (
                    <img src={fileUrl(primary.url)} alt={`${w.brand} ${w.model}`} className="h-full w-full object-cover" />
                  ) : (
                    <ImageOff size={20} className="text-[var(--muted)]" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[var(--text)] truncate">{w.brand} {w.model}</div>
                  <div className="text-xs text-[var(--muted)] truncate">{w.reference_number ? `Ref ${w.reference_number}` : "No reference"}</div>
                  <div className="mt-2">
                    {canManage ? (
                      busyId === w.id ? (
                        <span className="text-xs text-[var(--muted)]">Updating…</span>
                      ) : (
                        <FileUpload
                          visibility="public"
                          category="watch"
                          multiple={false}
                          label="Swap Primary"
                          testId={`cms-watch-swap-${w.id}`}
                          onUploaded={(fs) => fs[0] && swap(w, fs[0])}
                        />
                      )
                    ) : (
                      <span className="text-xs text-[var(--muted)]">View only</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
